import { useDroppable } from "@dnd-kit/core";
import type { ReactNode } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconTrash,
} from "@/components/icons";
import { IconButton } from "@/components/admin/IconButton";
import { SwitchControl, TextInput } from "@/components/admin/AdminShell";
import { InlineEditable } from "@/components/inline/InlineEditable";
import type { ApiFieldTypeOption } from "@/lib/formBuilder/apiFieldTypes";
import { collectPersonalDataTypes } from "@/lib/formBuilder/privacySuggestion";
import type {
  FormBuilderDraft,
  FormBuilderField,
  FormBuilderSection,
} from "@/types/formBuilder";
import { AiGenerateFieldsPanel } from "./AiGenerateFieldsPanel";
import { FormPreview } from "./FormPreview";
import { PrivacySuggestionBanner } from "./PrivacySuggestionBanner";
import { FieldDndProvider, SortableFieldList } from "./SortableFieldList";
import type { FormThemesCatalog } from "@/types/appearance";
import type { ValuesByName } from "@/types/visibility";

interface FormBuilderFormTabProps {
  draft: FormBuilderDraft;
  numericId: number | null;
  publicId: string | null;
  isNew: boolean;
  isYandex: boolean;
  canManage: boolean;
  fieldTypes: ApiFieldTypeOption[];
  themesCatalog: FormThemesCatalog;
  isGenerating: boolean;
  generateError: string | null;
  showPrivacySuggestion: boolean;
  detectedPersonalDataTypes: ReturnType<typeof collectPersonalDataTypes>;
  allFieldNames: string[];
  allBuilderFields: FormBuilderField[];
  previewValuesByName: ValuesByName;
  onPreviewFieldValueChange: (fieldName: string, value: string) => void;
  onUpdateSection: (
    sectionClientId: string,
    patch: Partial<FormBuilderSection>,
  ) => void;
  onUpdateField: (
    sectionClientId: string,
    fieldClientId: string,
    patch: Partial<FormBuilderField>,
  ) => void;
  onAddField: (sectionClientId: string) => void;
  onRemoveField: (sectionClientId: string, fieldClientId: string) => void;
  onMoveField: (
    sectionClientId: string,
    fieldClientId: string,
    direction: -1 | 1,
  ) => void;
  onDropField: (
    fieldClientId: string,
    sectionClientId: string,
    targetIndex: number,
  ) => void;
  onDuplicateField: (sectionClientId: string, fieldClientId: string) => void;
  onMoveSection: (sectionClientId: string, direction: -1 | 1) => void;
  onAddSection: () => void;
  onRemoveSection: (sectionClientId: string) => void;
  onUpdateDraft: (patch: Partial<FormBuilderDraft>) => void;
  onGenerateFields: (prompt: string, mode: "replace" | "append") => void;
  onAddPrivacyField: () => void;
  onDismissPrivacySuggestion: () => void;
}

function DroppableSection({
  sectionClientId,
  disabled,
  children,
}: {
  sectionClientId: string;
  disabled: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `section:${sectionClientId}`,
    disabled,
  });

  return (
    <section
      ref={setNodeRef}
      className={`relative mb-4 mt-3 rounded-xl border bg-white px-4 pb-5 pt-5 shadow-card-lg ${
        isOver
          ? "border-brand-400 ring-2 ring-brand-100"
          : "border-slate-200/80"
      }`}
    >
      {children}
    </section>
  );
}

export function FormBuilderFormTab({
  draft,
  numericId,
  publicId,
  isNew,
  isYandex,
  canManage,
  fieldTypes,
  themesCatalog,
  isGenerating,
  generateError,
  showPrivacySuggestion,
  detectedPersonalDataTypes,
  allFieldNames,
  allBuilderFields,
  previewValuesByName,
  onPreviewFieldValueChange,
  onUpdateSection,
  onUpdateField,
  onAddField,
  onRemoveField,
  onMoveField,
  onDropField,
  onDuplicateField,
  onMoveSection,
  onAddSection,
  onRemoveSection,
  onUpdateDraft,
  onGenerateFields,
  onAddPrivacyField,
  onDismissPrivacySuggestion,
}: FormBuilderFormTabProps) {
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="space-y-4">
        {!isYandex ? (
          <>
            <AiGenerateFieldsPanel
              formName={draft.name}
              hasExistingFields={draft.sections.some(
                (section) => section.fields.length > 0,
              )}
              isGenerating={isGenerating}
              error={generateError}
              onGenerate={onGenerateFields}
            />
            {showPrivacySuggestion ? (
              <PrivacySuggestionBanner
                detectedTypes={detectedPersonalDataTypes}
                onAdd={onAddPrivacyField}
                onDismiss={onDismissPrivacySuggestion}
              />
            ) : null}
          </>
        ) : null}

        {!canManage && !isNew ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="font-onest text-sm text-slate-600">
              Режим просмотра — у вас нет прав на редактирование этой формы.
            </p>
          </div>
        ) : null}

        {!isYandex ? (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-slate-200 pb-4">
            <fieldset disabled={!canManage} className="flex items-center gap-3">
              <legend className="sr-only">Заголовки разделов</legend>
              <label
                htmlFor="show-section-labels"
                className="font-onest text-sm font-medium text-ink"
              >
                Заголовки разделов
              </label>
              <SwitchControl
                id="show-section-labels"
                compact
                checked={draft.showSectionLabels}
                onChange={(showSectionLabels) => {
                  if (canManage) onUpdateDraft({ showSectionLabels });
                }}
                checkedClassName="bg-emerald-500"
                uncheckedClassName="bg-slate-300"
              />
            </fieldset>
            <fieldset disabled={!canManage} className="flex items-center gap-3">
              <legend className="sr-only">Ответы после отправки</legend>
              <label
                htmlFor="show-submission-after-submit"
                className="font-onest text-sm font-medium text-ink"
              >
                Показать ответы после отправки
              </label>
              <SwitchControl
                id="show-submission-after-submit"
                compact
                checked={draft.showSubmissionAfterSubmit}
                onChange={(showSubmissionAfterSubmit) => {
                  if (canManage) onUpdateDraft({ showSubmissionAfterSubmit });
                }}
                ariaLabel="Показать ответы после отправки"
                checkedClassName="bg-emerald-500"
                uncheckedClassName="bg-slate-300"
              />
            </fieldset>
            <fieldset disabled={!canManage} className="flex items-center gap-3">
              <legend className="sr-only">Режим формы</legend>
              <span
                aria-hidden="true"
                className="font-onest text-sm font-medium text-ink"
              >
                Режим
              </span>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white p-0.5">
                {(
                  [
                    ["form", "Форма"],
                    ["chatbot", "Чат"],
                    ["quiz", "Квиз"],
                  ] as const
                ).map(([mode, label]) => (
                  <label key={mode} className="cursor-pointer">
                    <input
                      type="radio"
                      name="interaction-mode"
                      value={mode}
                      checked={draft.interactionMode === mode}
                      onChange={() => onUpdateDraft({ interactionMode: mode })}
                      className="peer sr-only"
                    />
                    <span className="block rounded px-2.5 py-1.5 font-onest text-xs font-medium text-slate-600 peer-checked:bg-brand-50 peer-checked:text-brand-700 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-brand-500">
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {draft.interactionMode === "quiz" ? (
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center">
                <fieldset disabled={!canManage} className="flex items-center gap-3">
                  <legend className="sr-only">Когда показывать верно или неверно</legend>
                  <span className="font-onest text-sm font-medium text-ink">
                    Проверка
                  </span>
                  <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white p-0.5">
                    {(
                      [
                        ["end", "В конце"],
                        ["per_question", "После вопроса"],
                      ] as const
                    ).map(([scoring, label]) => (
                      <label key={scoring} className="cursor-pointer">
                        <input
                          type="radio"
                          name="quiz-scoring"
                          value={scoring}
                          checked={draft.quiz.scoring === scoring}
                          onChange={() =>
                            onUpdateDraft({
                              quiz: { ...draft.quiz, scoring },
                            })
                          }
                          className="peer sr-only"
                        />
                        <span className="block rounded px-2.5 py-1.5 font-onest text-xs font-medium text-slate-600 peer-checked:bg-brand-50 peer-checked:text-brand-700 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-brand-500">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset disabled={!canManage} className="flex items-center gap-3">
                  <label
                    htmlFor="quiz-show-correct"
                    className="font-onest text-sm font-medium text-ink"
                  >
                    Показать верный ответ
                  </label>
                  <SwitchControl
                    id="quiz-show-correct"
                    compact
                    checked={draft.quiz.showCorrectAnswers}
                    onChange={(showCorrectAnswers) =>
                      onUpdateDraft({
                        quiz: { ...draft.quiz, showCorrectAnswers },
                      })
                    }
                    ariaLabel="Показать верный ответ"
                    checkedClassName="bg-emerald-500"
                    uncheckedClassName="bg-slate-300"
                  />
                </fieldset>
                <fieldset disabled={!canManage} className="flex items-center gap-2">
                  <label
                    htmlFor="quiz-pass-score"
                    className="font-onest text-sm font-medium text-ink"
                  >
                    Порог, %
                  </label>
                  <TextInput
                    id="quiz-pass-score"
                    size="compact"
                    type="number"
                    className="w-20"
                    placeholder="нет"
                    value={
                      draft.quiz.passScore == null
                        ? ""
                        : String(draft.quiz.passScore)
                    }
                    onChange={(value) => {
                      const trimmed = value.trim();
                      if (!trimmed) {
                        onUpdateDraft({
                          quiz: { ...draft.quiz, passScore: null },
                        });
                        return;
                      }
                      const parsed = Number(trimmed);
                      onUpdateDraft({
                        quiz: {
                          ...draft.quiz,
                          passScore: Number.isFinite(parsed)
                            ? Math.min(100, Math.max(0, parsed))
                            : null,
                        },
                      });
                    }}
                  />
                </fieldset>
              </div>
            ) : null}
            <fieldset disabled={!canManage} className="flex items-center gap-3">
              <legend className="sr-only">Показ разделов</legend>
              <span
                aria-hidden="true"
                className="font-onest text-sm font-medium text-ink"
              >
                Показ разделов
              </span>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white p-0.5">
                {(
                  [
                    ["all", "Все сразу"],
                    ["step", "По шагам"],
                  ] as const
                ).map(([mode, label]) => (
                  <label key={mode} className="cursor-pointer">
                    <input
                      type="radio"
                      name="section-display-mode"
                      value={mode}
                      checked={draft.sectionDisplayMode === mode}
                      onChange={() =>
                        onUpdateDraft({ sectionDisplayMode: mode })
                      }
                      className="peer sr-only"
                    />
                    <span className="block rounded px-2.5 py-1.5 font-onest text-xs font-medium text-slate-600 peer-checked:bg-brand-50 peer-checked:text-brand-700 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-brand-500">
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        ) : null}

        <FieldDndProvider
          sections={draft.sections}
          fieldTypes={fieldTypes}
          onDrop={onDropField}
        >
          {draft.sections.map((section, sectionIndex) => (
            <DroppableSection
              key={section.clientId}
              sectionClientId={section.clientId}
              disabled={!canManage}
            >
              <div className="absolute left-1/2 top-0 z-10 w-[min(70%,28rem)] -translate-x-1/2 -translate-y-1/2 px-2 text-center">
                <InlineEditable
                  value={section.label}
                  placeholder="Название раздела"
                  onChange={(label) =>
                    onUpdateSection(section.clientId, { label })
                  }
                  className="block max-w-full truncate font-onest text-sm font-semibold text-ink"
                  emptyClassName="block max-w-full truncate font-onest text-sm font-medium text-slate-400"
                  inputClassName="w-full min-w-0 text-center text-sm"
                />
              </div>

              <div className="mb-2 flex min-h-8 items-center justify-end">
                {draft.sections.length > 1 && !isYandex ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <IconButton
                      title="Раздел выше"
                      size="sm"
                      disabled={!canManage || sectionIndex === 0}
                      onClick={() => onMoveSection(section.clientId, -1)}
                    >
                      <IconChevronUp size={14} />
                    </IconButton>
                    <IconButton
                      title="Раздел ниже"
                      size="sm"
                      disabled={
                        !canManage || sectionIndex === draft.sections.length - 1
                      }
                      onClick={() => onMoveSection(section.clientId, 1)}
                    >
                      <IconChevronDown size={14} />
                    </IconButton>
                    <IconButton
                      title="Удалить раздел"
                      size="sm"
                      variant="danger"
                      disabled={!canManage}
                      onClick={() => onRemoveSection(section.clientId)}
                    >
                      <IconTrash size={14} />
                    </IconButton>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <SortableFieldList
                  fields={section.fields}
                  fieldNames={allFieldNames}
                  builderFields={allBuilderFields}
                  fieldTypes={fieldTypes}
                  quizMode={draft.interactionMode === "quiz"}
                  canDrag={canManage}
                  onChange={(fieldClientId, patch) =>
                    onUpdateField(section.clientId, fieldClientId, patch)
                  }
                  onRemove={(fieldClientId) =>
                    onRemoveField(section.clientId, fieldClientId)
                  }
                  onMove={(fieldClientId, direction) =>
                    onMoveField(section.clientId, fieldClientId, direction)
                  }
                  canDuplicate={canManage}
                  onDuplicate={(fieldClientId) =>
                    onDuplicateField(section.clientId, fieldClientId)
                  }
                />
              </div>

              <button
                type="button"
                onClick={() => onAddField(section.clientId)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-brand-300 bg-brand-50/30 px-3 py-2 font-onest text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50"
              >
                <IconPlus size={14} />
                Добавить поле
              </button>

              {!isYandex && sectionIndex === draft.sections.length - 1 ? (
                <button
                  type="button"
                  onClick={onAddSection}
                  className="absolute bottom-0 left-1/2 inline-flex -translate-x-1/2 translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 font-onest text-xs font-medium text-slate-600 transition-colors hover:text-brand-700"
                >
                  <IconPlus size={14} />
                  Добавить раздел
                </button>
              ) : null}
            </DroppableSection>
          ))}
        </FieldDndProvider>
      </div>

      <FormPreview
        draft={draft}
        themesCatalog={themesCatalog}
        valuesByName={previewValuesByName}
        onFieldValueChange={onPreviewFieldValueChange}
        formId={numericId}
        publicId={publicId}
      />
    </div>
  );
}
